import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { RootStore, withInjectedRootStore } from 'providers';
import { useTranslation } from 'react-i18next';
import { CyclesController } from '../../../pages/SubscriptionPage/Cycles/Cycles.controller';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { PaymentCycle } from '@wix/package-picker-common';
import styles from './HeaderCycleSelectorDropdown.module.scss';
import { Box, Dropdown, listItemSelectBuilder, Text } from '@wix/design-system';
import classNames from 'classnames';

interface CycleSelectorDropdownCompProps extends InjectedExperimentsProps {
  rootStore: RootStore;
}

const CycleSelectorDropdownComp: React.FC<CycleSelectorDropdownCompProps> =
  observer(({ rootStore, experiments }) => {
    const { t } = useTranslation();
    const controller = React.useMemo(
      () => new CyclesController({ rootStore, experiments }),
      [rootStore, experiments],
    );
    const cycles = controller.getCycles(
      controller.getCurrentOrDefaultProduct(),
    );

    const [selected, setSelected] = React.useState<PaymentCycle>(
      rootStore.appStore.currentCycle,
    );

    const inputRef = React.useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (selected !== rootStore.appStore.currentCycle) {
        setSelected(rootStore.appStore.currentCycle);
      }
    }, [rootStore.appStore.currentCycle]);

    const options = cycles.map(({ cycleDetails }) => {
      const disabled = controller.isCycleDisabledInPlansPage(cycleDetails);
      const title = t(
        CyclesController.getCycleNameKey(
          cycleDetails.cycleId,
          rootStore.appStore.isJapanesePackagePicker,
        ),
      );
      return {
        id: cycleDetails.cycleId,
        title,
        value: title,
        disabled,
        suffix: (
          <span
            onClick={() => {
              inputRef.current?.click();
            }}
            className={classNames(styles.savings, {
              [styles.savingsLabel]:
                cycleDetails.fullTermDisplaySavings &&
                !controller.isCycleDisabledInPlansPage(cycleDetails),
              [styles.sale]: rootStore.appStore.isInSale,
              [styles.disabled]: disabled,
              [styles.selected]: selected === cycleDetails.cycleId,
            })}
          >
            {controller.getCycleSavingsLabel(cycleDetails, t)}
          </span>
        ),
      };
    });

    const selectedOpt = options.find((opt) => opt.id === selected);

    return (
      <Dropdown
        suffix={selectedOpt?.suffix}
        selectedId={selected}
        inputRef={(el) => {
          inputRef.current = el;
        }}
        valueParser={() => selectedOpt?.value}
        border='standard'
        options={options.map(listItemSelectBuilder)}
        onSelect={(option) => {
          setSelected(option.id as PaymentCycle);
          rootStore.appStore.setCurrentCycle({
            cycle: option.id as PaymentCycle,
            sendBiEvent: true,
          });
        }}
        dataHook='cycle-selector-dropdown'
        className={styles.dropdown}
      />
    );
  });

const CycleSelectorDropdown = withInjectedRootStore(
  withExperiments(CycleSelectorDropdownComp),
);

export const HeaderCycleSelectorDropdown = () => {
  const { t } = useTranslation();
  return (
    <Box display='flex' height='100%'>
      <Box className={styles.headerContainer}>
        <Text className={styles.headerLabel}>
          {t(
            'premium-purchase-plan.package_picker_and_cycle_selector.feature_table.billing_cycle_title',
          )}
        </Text>
        <CycleSelectorDropdown />
      </Box>
    </Box>
  );
};
